import { Button } from "@/components/ui/button";
import { Zap, Lightbulb, Target, MessageSquare, HelpCircle, AlertTriangle, ArrowRight, User, Building, Users, ExternalLink, Video } from "lucide-react";
import { openExternal } from "./calendarFormat";

const SectionTitle = ({ icon: Icon, color, children }) => (
  <h4 className={`text-xs ${color} tracking-wider mb-2 flex items-center gap-1`}><Icon className="h-3 w-3" /> {children}</h4>
);

const Callout = ({ icon, color, title, boxClass, children }) => (
  <div className={boxClass}>
    <SectionTitle icon={icon} color={color}>{title}</SectionTitle>
    <p className="text-sm text-gray-200">{children}</p>
  </div>
);

const ListSection = ({ icon, color, title, items, marker, itemClass = "" }) => (
  <div>
    <SectionTitle icon={icon} color={color}>{title}</SectionTitle>
    <div className="space-y-2">
      {items.map((item, i) => (
        <div key={item} className={`flex items-start gap-2 text-sm text-gray-300 ${itemClass}`}>
          <span className={color}>{marker(i)}</span>
          <span>{item}</span>
        </div>
      ))}
    </div>
  </div>
);

const ExtLink = ({ href, label }) => (
  <a href={href} target="_blank" rel="noopener noreferrer" className="text-xs text-ism-teal hover:text-ism-teal-light flex items-center gap-1">
    <ExternalLink className="h-3 w-3" /> {label}
  </a>
);

const PersonIntel = ({ items }) => (
  <div>
    <SectionTitle icon={User} color="text-gray-400">Person Intelligence</SectionTitle>
    <div className="space-y-3">
      {items.map((intel, i) => (
        <div key={intel.name || i} className="bg-gray-600/5 border border-gray-600/20 rounded-lg p-3">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-gray-300">{intel.name}</span>
            {intel.linkedin_url && <ExtLink href={intel.linkedin_url} label="LinkedIn" />}
          </div>
          <span className="text-xs text-gray-500 block mb-1">@ {intel.company}</span>
          {(intel.background || intel.role_context) && <p className="text-xs text-gray-400 mt-1">{intel.background || intel.role_context}</p>}
        </div>
      ))}
    </div>
  </div>
);

const CompanyIntel = ({ items }) => (
  <div>
    <SectionTitle icon={Building} color="text-gray-400">Company Intelligence</SectionTitle>
    <div className="space-y-3">
      {items.map((intel, i) => (
        <div key={intel.name || i} className="bg-gray-600/5 border border-gray-600/20 rounded-lg p-3">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-gray-300">{intel.name}</span>
            {intel.website && <ExtLink href={intel.website.startsWith("http") ? intel.website : `https://${intel.website}`} label="Website" />}
          </div>
          {intel.overview && <p className="text-xs text-gray-400 mb-2">{intel.overview}</p>}
          {intel.recent_news?.length > 0 && (
            <div className="mt-2 pt-2 border-t border-white/10">
              <span className="text-xs text-gray-500 block mb-1">Recent News:</span>
              {intel.recent_news.slice(0, 2).map((news, j) => (
                <div key={news.url || news.link || news.title || j} className="text-xs text-gray-400 mb-1">• {news.title}</div>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  </div>
);

export const BriefDetails = ({ brief }) => (
  <div className="p-4 space-y-4">
    {brief.executive_summary && (
      <Callout icon={Zap} color="text-gray-400" title="Executive Summary" boxClass="bg-gradient-to-r from-gray-600/10 to-ism-teal/10 border border-gray-600/20 rounded-lg p-3">{brief.executive_summary}</Callout>
    )}
    {brief.consultative_pov && (
      <div>
        <SectionTitle icon={Lightbulb} color="text-ism-teal">Your Consultative Point of View</SectionTitle>
        <p className="text-sm text-gray-300 bg-ism-teal/5 border border-ism-teal/20 rounded-lg p-3">{brief.consultative_pov}</p>
      </div>
    )}
    {brief.deal_strategy && (
      <div>
        <SectionTitle icon={Target} color="text-amber-400">Recommended Strategy</SectionTitle>
        <p className="text-sm text-gray-300 bg-amber-500/5 border border-amber-500/20 rounded-lg p-3">{brief.deal_strategy}</p>
      </div>
    )}
    {brief.talking_points?.length > 0 && (
      <ListSection icon={MessageSquare} color="text-emerald-400" title="Key Talking Points" items={brief.talking_points} marker={(i) => `${i + 1}.`} itemClass="bg-emerald-500/5 border border-emerald-500/20 rounded-lg p-2" />
    )}
    {brief.questions_to_ask?.length > 0 && (
      <ListSection icon={HelpCircle} color="text-ism-teal" title="Discovery Questions" items={brief.questions_to_ask} marker={() => "?"} />
    )}
    {brief.risks_and_objections?.length > 0 && (
      <ListSection icon={AlertTriangle} color="text-red-400" title="Prepare For" items={brief.risks_and_objections} marker={() => "!"} itemClass="bg-red-500/5 border border-red-500/20 rounded-lg p-2" />
    )}
    {brief.next_steps_recommendation && (
      <Callout icon={ArrowRight} color="text-amber-400" title="Recommended Next Step" boxClass="bg-gradient-to-r from-amber-500/10 to-orange-500/10 border border-amber-500/20 rounded-lg p-3">{brief.next_steps_recommendation}</Callout>
    )}
    {brief.attendee_intelligence?.length > 0 && <PersonIntel items={brief.attendee_intelligence} />}
    {brief.company_intelligence?.length > 0 && <CompanyIntel items={brief.company_intelligence} />}
    {brief.attendees?.length > 0 && (
      <div>
        <SectionTitle icon={Users} color="text-gray-500">Attendees</SectionTitle>
        <div className="flex flex-wrap gap-2">
          {brief.attendees.map((attendee, i) => (
            <div key={attendee.email || attendee.name || i} className="flex items-center gap-2 bg-white/5 rounded-lg px-2 py-1">
              <span className="text-xs text-gray-300">{attendee.name || attendee.email}</span>
              {attendee.company && <span className="text-xs text-gray-500">@ {attendee.company}</span>}
            </div>
          ))}
        </div>
      </div>
    )}
    {brief.meeting_links?.length > 0 && (
      <div className="pt-2 border-t border-white/10">
        <Button onClick={() => openExternal(brief.meeting_links[0].url)} size="sm" className="bg-gradient-to-r from-ism-teal to-ism-teal hover:from-ism-teal hover:to-ism-teal-light text-white text-xs rounded-lg">
          <Video className="h-3 w-3 mr-1" /> Join Meeting
        </Button>
      </div>
    )}
  </div>
);
